import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, CheckCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ManualEmailBookingPage() {
  const navigate = useNavigate();
  const { emailId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [busySlots, setBusySlots] = useState([]);
  const [loadingBusySlots, setLoadingBusySlots] = useState(false);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    room_id: '',
    meeting_name: '',
    organizer_email: '',
    participant_emails: '',
    date: '',
    start_time: '',
    end_time: ''
  });

  useEffect(() => {
    fetchData();
  }, [emailId]);

  useEffect(() => {
    fetchBusySlots();
  }, [formData.room_id, formData.date]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/email/recent/${emailId}/manual`);
      const payload = response.data;
      setEmailData(payload);
      setRooms(payload.rooms || []);
      setFormData({
        room_id: payload.rooms?.[0]?.id || '',
        meeting_name: payload.meeting_name || '',
        organizer_email: payload.organizer_email || '',
        participant_emails: (payload.participant_emails || []).join(', '),
        date: '',
        start_time: '',
        end_time: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel carregar o email');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value
      };

      if (field === 'room_id' || field === 'date') {
        next.start_time = '';
        next.end_time = '';
      }

      if (field === 'start_time' && next.end_time) {
        const nextStartMinutes = timeToMinutes(value);
        const nextEndMinutes = timeToMinutes(next.end_time);
        if (nextStartMinutes !== null && nextEndMinutes !== null && nextEndMinutes <= nextStartMinutes) {
          next.end_time = '';
        }
      }

      return next;
    });
  };

  const fetchBusySlots = async () => {
    if (!formData.room_id || !formData.date) {
      setBusySlots([]);
      setLoadingBusySlots(false);
      return;
    }

    try {
      setLoadingBusySlots(true);
      const response = await axios.get(`${API}/rooms/${formData.room_id}/busy-slots`, {
        params: { date: formData.date }
      });
      setBusySlots(response.data.busy_slots || []);
    } catch (error) {
      setBusySlots([]);
      toast.error(error.response?.data?.detail || 'Nao foi possivel carregar os horarios ocupados');
    } finally {
      setLoadingBusySlots(false);
    }
  };

  const formatSlotTime = (value) => new Date(value).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  });
  const timeOptions = Array.from({ length: 96 }, (_, index) => {
    const hours = String(Math.floor(index / 4)).padStart(2, '0');
    const minutes = String((index % 4) * 15).padStart(2, '0');
    return `${hours}:${minutes}`;
  });
  const timeToMinutes = (timeValue) => {
    if (!timeValue || !timeValue.includes(':')) {
      return null;
    }
    const [hours, minutes] = timeValue.split(':').map(Number);
    return (hours * 60) + minutes;
  };
  const roundUpToQuarter = (dateValue) => {
    const result = new Date(dateValue);
    result.setSeconds(0, 0);
    const remainder = result.getMinutes() % 15;
    if (remainder !== 0) {
      result.setMinutes(result.getMinutes() + (15 - remainder));
    }
    return result;
  };

  const canChooseTime = Boolean(formData.room_id && formData.date);
  const canInteractWithTime = canChooseTime && !loadingBusySlots;
  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const isTodaySelected = formData.date === minDate;
  const minimumSelectableMinutes = isTodaySelected ? ((roundUpToQuarter(today).getHours() * 60) + roundUpToQuarter(today).getMinutes()) : 0;
  const selectedRoomName = rooms.find((room) => room.id === formData.room_id)?.name || '';
  const selectedStartDateTime = canChooseTime && formData.start_time ? new Date(`${formData.date}T${formData.start_time}:00`) : null;
  const selectedEndDateTime = canChooseTime && formData.end_time ? new Date(`${formData.date}T${formData.end_time}:00`) : null;
  const startTimeMinutes = timeToMinutes(formData.start_time);
  const endTimeMinutes = timeToMinutes(formData.end_time);
  const conflictingSlots = busySlots.filter((slot) => {
    if (!selectedStartDateTime || !selectedEndDateTime) {
      return false;
    }
    const slotStart = new Date(slot.start_time);
    const slotEnd = new Date(slot.end_time);
    return selectedStartDateTime < slotEnd && selectedEndDateTime > slotStart;
  });
  const busyRanges = busySlots.map((slot) => {
    const slotStart = new Date(slot.start_time);
    const slotEnd = new Date(slot.end_time);
    return {
      startMinutes: (slotStart.getHours() * 60) + slotStart.getMinutes(),
      endMinutes: (slotEnd.getHours() * 60) + slotEnd.getMinutes()
    };
  });
  const availableStartOptions = timeOptions.filter((timeOption) => {
    const optionMinutes = timeToMinutes(timeOption);
    if (optionMinutes === null || optionMinutes < minimumSelectableMinutes) {
      return false;
    }
    return !busyRanges.some((range) => optionMinutes >= range.startMinutes && optionMinutes < range.endMinutes);
  });
  const availableEndOptions = timeOptions.filter((timeOption) => {
    const optionMinutes = timeToMinutes(timeOption);
    if (optionMinutes === null || optionMinutes < minimumSelectableMinutes) {
      return false;
    }
    if (startTimeMinutes !== null && optionMinutes <= startTimeMinutes) {
      return false;
    }
    return !busyRanges.some((range) => optionMinutes > range.startMinutes && optionMinutes <= range.endMinutes);
  });
  const timeValidationMessage = startTimeMinutes !== null && endTimeMinutes !== null && endTimeMinutes <= startTimeMinutes
    ? 'O horario de termino deve ser maior que o horario de inicio'
    : '';
  const generatedDescription = [
    `Titulo: ${formData.meeting_name || '-'}`,
    `Sala: ${selectedRoomName || '-'}`,
    `Organizador: ${formData.organizer_email || '-'}`,
    `Participantes: ${formData.participant_emails || '-'}`,
    `Data: ${formData.date || '-'}`,
    `Inicio: ${formData.start_time || '-'}`,
    `Fim: ${formData.end_time || '-'}`,
    `Assunto original: ${emailData?.subject || '-'}`,
    `Remetente original: ${emailData?.from_email || '-'}`
  ].join('\n');

  const isQuarterHour = (timeValue) => {
    if (!timeValue || !timeValue.includes(':')) {
      return false;
    }
    const [, minutes] = timeValue.split(':');
    return Number(minutes) % 15 === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const now = new Date();
    const startDateCheck = new Date(`${formData.date}T${formData.start_time}:00`);
    const endDateCheck = new Date(`${formData.date}T${formData.end_time}:00`);
    if (Number.isNaN(startDateCheck.getTime()) || startDateCheck < now) {
      toast.error('O horario de inicio nao pode ser menor que a data e hora atual');
      return;
    }
    if (Number.isNaN(endDateCheck.getTime()) || endDateCheck < now) {
      toast.error('O horario de termino nao pode ser menor que a data e hora atual');
      return;
    }
    if (endDateCheck <= startDateCheck) {
      toast.error('O horario de termino deve ser maior que o horario de inicio');
      return;
    }
    if (!isQuarterHour(formData.start_time) || !isQuarterHour(formData.end_time)) {
      toast.error('Os horarios devem ser selecionados em intervalos de 15 minutos');
      return;
    }
    if (loadingBusySlots) {
      toast.error('Aguarde o carregamento dos horarios agendados da sala');
      return;
    }
    setSaving(true);
    try {
      const participantEmails = formData.participant_emails
        .split(/[\n,;]+/)
        .map((item) => item.trim())
        .filter(Boolean);

      const startDateTime = `${formData.date}T${formData.start_time}:00-03:00`;
      const endDateTime = `${formData.date}T${formData.end_time}:00-03:00`;

      const response = await axios.post(`${API}/email/recent/${emailId}/manual-booking`, {
        room_id: formData.room_id,
        meeting_name: formData.meeting_name,
        organizer_email: formData.organizer_email,
        participant_emails: participantEmails,
        start_time: startDateTime,
        end_time: endDateTime,
        description: generatedDescription
      });

      setSuccess(response.data);
      toast.success(response.data.message || 'Reuniao criada manualmente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel criar a reuniao');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-2xl w-full bg-white rounded-3xl border border-border/40 p-10 shadow-xl text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-manrope font-bold tracking-tight mb-3">Reuniao criada</h1>
          <p className="text-muted-foreground mb-6">
            A reserva foi criada e o convite .ics foi enviado aos participantes.
          </p>
          <div className="text-left bg-gray-50 rounded-2xl p-5 space-y-2">
            <p><strong>Sala:</strong> {success.room_name}</p>
            <p><strong>Inicio:</strong> {new Date(success.start_time).toLocaleString('pt-BR')}</p>
            <p><strong>Fim:</strong> {new Date(success.end_time).toLocaleString('pt-BR')}</p>
          </div>
          <Button onClick={() => navigate('/configuracoes/email')} className="rounded-full mt-6">
            Voltar para Email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/configuracoes/email')} variant="ghost" size="sm" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-manrope font-bold tracking-tight">Criar Reuniao Manualmente</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10 space-y-8">
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm">
          <h2 className="text-lg font-manrope font-bold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Email de origem
          </h2>
          <div className="space-y-2 text-sm">
            <p><strong>Assunto:</strong> {emailData?.subject}</p>
            <p><strong>Remetente:</strong> {emailData?.from_email || '-'}</p>
            <p><strong>Cc:</strong> {emailData?.cc || '-'}</p>
            <p><strong>ICS detectado:</strong> {emailData?.has_ics ? 'Sim' : 'Nao'}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Titulo da reuniao</Label>
                <Input value={formData.meeting_name} onChange={(e) => handleChange('meeting_name', e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Sala</Label>
                <select
                  value={formData.room_id}
                  onChange={(e) => handleChange('room_id', e.target.value)}
                  required
                  className="w-full h-10 border border-border rounded-md px-3 bg-white"
                >
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>{room.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Organizador</Label>
                <Input
                  type="email"
                  value={formData.organizer_email}
                  onChange={(e) => handleChange('organizer_email', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" min={minDate} value={formData.date} onChange={(e) => handleChange('date', e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Hora inicio</Label>
                <select
                  value={formData.start_time}
                  onChange={(e) => handleChange('start_time', e.target.value)}
                  required
                  disabled={!canInteractWithTime}
                  className="w-full h-10 border border-border rounded-md px-3 bg-white disabled:bg-gray-100"
                >
                  <option value="">Selecione</option>
                  {availableStartOptions.map((timeOption) => (
                    <option key={timeOption} value={timeOption}>{timeOption}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Hora fim</Label>
                <select
                  value={formData.end_time}
                  onChange={(e) => handleChange('end_time', e.target.value)}
                  required
                  disabled={!canInteractWithTime}
                  className="w-full h-10 border border-border rounded-md px-3 bg-white disabled:bg-gray-100"
                >
                  <option value="">Selecione</option>
                  {availableEndOptions.map((timeOption) => (
                    <option key={timeOption} value={timeOption}>{timeOption}</option>
                  ))}
                </select>
              </div>
            </div>

            {timeValidationMessage && (
              <div className="rounded-xl border border-red-300 bg-red-50 px-3 py-3 text-sm font-medium text-red-700">
                {timeValidationMessage}
              </div>
            )}

            <div className="rounded-2xl border border-border/60 bg-gray-50 px-4 py-4 space-y-3">
              <div>
                <h3 className="font-manrope font-semibold">Horarios ja reservados</h3>
                <p className="text-sm text-muted-foreground">
                  Os campos de horario sao liberados apenas depois de selecionar a sala e a data.
                </p>
              </div>

              {!canChooseTime ? (
                <p className="text-sm text-muted-foreground">
                  Selecione a sala e a data para carregar os horarios ocupados do dia.
                </p>
              ) : loadingBusySlots ? (
                <p className="text-sm text-muted-foreground">
                  Carregando horarios agendados da sala selecionada...
                </p>
              ) : busySlots.length === 0 ? (
                <p className="text-sm text-green-700">
                  Nenhum horario reservado para esta sala na data selecionada.
                </p>
              ) : (
                <div className="space-y-2">
                  {selectedStartDateTime && selectedEndDateTime && conflictingSlots.length > 0 && (
                    <div className="rounded-xl border border-red-300 bg-red-50 px-3 py-3 text-sm font-medium text-red-700">
                      Ja existe reuniao agendada dentro da faixa de horario selecionada.
                    </div>
                  )}
                  {busySlots.map((slot) => (
                    <div
                      key={slot.booking_id}
                      className={`rounded-xl border px-3 py-3 ${
                        conflictingSlots.some((conflict) => conflict.booking_id === slot.booking_id)
                          ? 'bg-red-100 border-red-300 animate-pulse'
                          : 'bg-white border-border/60'
                      }`}
                    >
                      <p className="font-medium">
                        {formatSlotTime(slot.start_time)} - {formatSlotTime(slot.end_time)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Participantes</Label>
              <textarea
                value={formData.participant_emails}
                onChange={(e) => handleChange('participant_emails', e.target.value)}
                placeholder="Separe emails por virgula, ponto e virgula ou quebra de linha"
                className="w-full min-h-28 border border-border rounded-md px-3 py-2 bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label>Descricao gerada automaticamente</Label>
              <textarea
                value={generatedDescription}
                readOnly
                className="w-full min-h-36 border border-border rounded-md px-3 py-2 bg-gray-50 text-muted-foreground"
              />
            </div>

            <div className="flex gap-4">
              <Button type="submit" disabled={saving || loadingBusySlots} className="rounded-full">
                {saving ? 'Criando...' : 'Criar reuniao e enviar .ics'}
              </Button>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => navigate('/configuracoes/email')}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
